import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { UserContextService } from 'rev-shared/security/UserContext.Service';
import { IDragData } from 'rev-shared/ui/dragAndDrop/Draggable.AngularDirective';
import { IPresenter, StreamType } from 'rev-shared/webrtc/Contract';
import { getPercentPositionStyle } from 'rev-shared/webrtc/LayoutUtil';
import { WebRtcListenerConnectionService } from 'rev-shared/webrtc/WebRtcListenerConnection.Service';
import { Observable, of, Subscription } from 'rxjs';

import { IProducerLayoutVideo, IProducerStreamsCfg } from '../../Contract';
import { ProducerService } from '../../Producer.Service';
import { dragOptions } from '../controls/ProducerVideoControls.Component';

import styles from './VideoPreview.module.less';

@Component({
	selector: 'video-preview-player-tile',
	templateUrl: './VideoPreviewPlayerTile.Component.html',
	host: {
		'[style]': 'positionStyle'
	},
	standalone: false
})
export class VideoPreviewPlayerTileComponent implements OnChanges, OnDestroy, OnInit {
	@Input() public label: string;
	@Input() public cfg: IProducerLayoutVideo;
	@Input() public presenter: IPresenter;
	@Input() public isPreview: boolean;
	@Input() public index: number;
	@Input() public isProducer: boolean;

	public readonly styles = styles;
	public readonly dragOptions = dragOptions;
	public positionStyle: string = '';
	public stream: Observable<MediaStream>;
	private presenterStream: MediaStream;
	private localStreams: IProducerStreamsCfg;
	private readonly sub = new Subscription();

	constructor(
		private readonly ListenerConnection: WebRtcListenerConnectionService,
		private readonly UserContext: UserContextService,
		private readonly ProducerService: ProducerService
	)
	{}

	public ngOnInit(): void {
		this.sub.add(this.ProducerService.producerStreams$.subscribe(
			streams => {
				this.localStreams = streams;
				this.update();
			}
		));
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if(!changes.cfg && !changes.presenter) {
			return;
		}
		this.update();
	}

	public ngOnDestroy(): void {
		this.sub.unsubscribe();
	}

	private update(): void {
		const { userId, videoProducerId, rect } = this.cfg;
		this.positionStyle = !rect ? '' : getPercentPositionStyle(rect);

		this.stream = (userId && videoProducerId) ? this.getStream(userId, videoProducerId) : null;
	}

	private getStream(presenterId: string, producerId: string): Observable<MediaStream> {
		const presenters = this.ListenerConnection.presenters;
		if(this.UserContext.getUser().id === presenterId) {
			const producer = presenters.getProducer(presenterId, producerId);
			if(producer && !producer.consumer) {
				return this.cfg.type === StreamType.Content ?
					of(this.localStreams?.displayCaptureStream) :
					this.localStreams?.cameraOutputStream$;
			}
		}

		this.presenterStream = presenters.tryCreateProducerStream(presenterId, producerId, this.presenterStream);
		return of(this.presenterStream);
	}

	public remove(): void {
		if(!this.isPreview || !this.isProducer) {
			return;
		}

		this.ProducerService.clearPreviewLayoutSlot(this.index)
			.catch(e => console.error('VideoPreviewPlayerTile. remove: ', e));

	}

	public getDragData(): () => any {
		return () => {
			if(this.isPreview && this.isProducer && this.cfg.userId) {
				return { index: this.index };
			}
		};
	}

	public onDragStop(event: IDragData): void {
		if(!event.droppable && event.data.index >= 0) {
			this.remove();
		}
	}
}
